import { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { euro } from '../../core/format/currency';
import { btnIcon } from './styles';
import { move } from '../editing';
import type { Product } from '../../core/types/catalog';
type Props = {
  value: string[];
  onChange: (ids: string[]) => void;
  products: Product[];
  /** Volgorde telt (protocollen): toon pijltjes en nummers. */
  ordered?: boolean;
  /** Deze ids niet aanbieden (bijv. het product zelf). */
  exclude?: string[];
  emptyText?: string;
};

/** Zoveel zoekresultaten tegelijk; verfijn de zoekterm voor de rest. */
const MAX_RESULTS = 8;

/** Producten kiezen uit de catalogus, met zoeken en (optioneel) volgorde. */
export function ProductPicker({
  value,
  onChange,
  products,
  ordered = false,
  exclude = [],
  emptyText = 'Nog geen producten gekozen.'
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const byId = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(p => !value.includes(p.id) && !exclude.includes(p.id)).filter(p => !q || `${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS);
  }, [products, value, exclude, query]);
  return <div data-id="element-5110" data-mp="2fxhb11kfh51k">
      {value.length === 0 ? <p className="rounded-md bg-shell px-4 py-5 text-center text-[13px] text-muted" data-id="element-5111" data-mp="2azz055muofqx">{emptyText}</p> : <ol className="divide-y divide-line border-y border-line" data-id="element-5112" data-mp="2ppdbgj9zl78y">
          {value.map((id, i) => {
        const p = byId.get(id);
        const name = p?.name ?? id;
        return <li key={id} className="flex items-center gap-3 px-3 py-2" data-id="element-5113" data-mp="2yp0f7bq6up56" data-mpi={i}>
                {ordered && <span className="w-5 shrink-0 text-center font-display text-[15px] text-clay" aria-hidden="true" data-id="element-5114" data-mp="2tr8b7jc9rckm">
                    {i + 1}
                  </span>}
                {p?.image ? <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-5115" data-mp="1wcx40ho7nego" /> : <span className="h-10 w-10 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-5116" data-mp="20c5undnyew80" />}
                <span className="min-w-0 flex-1" data-id="element-5117" data-mp="255qpwxy5xc3i">
                  <span className="block truncate text-[14px] text-ink" data-id="element-5118" data-mp="2a4bo6rtutlpb">
                    {p ? p.name : <span className="text-clay" data-id="element-5119" data-mp="2f2d9zkinzz4h">Onbekend product: {id}</span>}
                  </span>
                  {p && <span className="block text-[12px] text-muted" data-id="element-5120" data-mp="2k3180q5b9g87">
                      {p.brand} · {p.step ?? p.category} · {euro(p.price)}
                    </span>}
                </span>
                {ordered && <span className="flex shrink-0" data-id="element-5121" data-mp="pq1nqjpzjxul">
                    <button type="button" onClick={() => onChange(move(value, i, i - 1))} disabled={i === 0} aria-label={`${name} omhoog`} className={btnIcon} data-id="element-5122" data-mp="tqbptlanq446">
                      <ArrowUpIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-5123" data-mp="hf16icsggqs9" />
                    </button>
                    <button type="button" onClick={() => onChange(move(value, i, i + 1))} disabled={i === value.length - 1} aria-label={`${name} omlaag`} className={btnIcon} data-id="element-5124" data-mp="cf6l2org8tel">
                      <ArrowDownIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-5125" data-mp="106lqwrifpsul" />
                    </button>
                  </span>}
                <button type="button" onClick={() => onChange(value.filter(x => x !== id))} aria-label={`${name} weghalen`} className={`shrink-0 ${btnIcon}`} data-id="element-5126" data-mp="r549n5tajl4b">
                  <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5127" data-mp="3gvhbzuj0l5mh" />
                </button>
              </li>;
      })}
        </ol>}

      <div className="relative mt-2" data-id="element-5128" data-mp="13lljccq9zn00">
        {open ? <div className="rounded-md border border-line bg-white shadow-lg" data-id="element-5129" data-mp="18cttxc3cdeer">
            <div className="flex items-center gap-2 border-b border-line px-3" data-id="element-5130" data-mp="18ey7klr1lrs8">
              <SearchIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5131" data-mp="unjoyg11rslp" />
              <input
          // Het zoekveld verschijnt pas na een klik op "Product toevoegen"; focus erheen is dan verwacht.
          autoFocus value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Escape' && setOpen(false)} placeholder="Zoek op naam, merk of soort…" aria-label="Product zoeken" className="flex-1 bg-transparent py-2.5 text-[16px] focus:outline-none sm:text-[14px]" data-id="element-5132" data-mp="ynk1fv4l5tq4" />
              <button type="button" onClick={() => setOpen(false)} className="text-[13px] text-muted hover:text-ink" data-id="element-5133" data-mp="6vurkl9dcdxw">
                Klaar
              </button>
            </div>
            <ul className="max-h-72 overflow-y-auto py-1" data-id="element-5134" data-mp="3umcjrvl2c0i5">
              {results.length === 0 && <li className="px-3 py-3 text-[13px] text-muted" data-id="element-5135" data-mp="3qkzb7dql5j7j">Niets gevonden.</li>}
              {results.map(p => <li key={p.id} data-id="element-5136" data-mp="3e6zcpkfjfou">
                  <button type="button" onClick={() => onChange([...value, p.id])} className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-5137" data-mp="3dx68t438sex">
                    <ProductImage src={p.image} className="h-9 w-9 shrink-0 rounded p-0.5" data-id="element-5138" data-mp="3lkblcve3f8ew" />
                    <span className="min-w-0 flex-1" data-id="element-5139" data-mp="3gtd1jltwbs6w">
                      <span className="block truncate text-[14px] text-ink" data-id="element-5140" data-mp="3grs85zjbifam">{p.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-5141" data-mp="2t1xvpj2wusqw">
                        {p.brand} · {p.step ?? p.category} · {euro(p.price)}
                      </span>
                    </span>
                    <PlusIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5142" data-mp="s0rlzkq78h6g" />
                  </button>
                </li>)}
            </ul>
          </div> : <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] text-clay transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-5143" data-mp="11rw0ykfvk21a">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5144" data-mp="e0qc5fryhzeu" />
            Product toevoegen
          </button>}
      </div>
    </div>;
}